import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { OmniLogo } from "../components/OmniLogo";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { usePageTitle } from "../lib/usePageTitle";
import { Search, MapPin, Loader2, Navigation, Compass, Route, Car, Bike, Footprints, X } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Default Leaflet markers fix (icons not loading from CDN by default in CRA build)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const POPULAR = [
  { label: "Tour Eiffel, Paris", q: "Tour Eiffel, Paris" },
  { label: "Times Square, NYC", q: "Times Square, New York" },
  { label: "Mont Fuji", q: "Mount Fuji" },
  { label: "Colisée, Rome", q: "Colosseum, Rome" },
];

function FlyTo({ center, zoom = 14 }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom, { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

function MapClicker({ onClick }) {
  useMapEvents({ click(e) { onClick([e.latlng.lat, e.latlng.lng]); } });
  return null;
}

function formatDuration(s) {
  if (!s && s !== 0) return "";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}
function formatDistance(m) {
  if (!m && m !== 0) return "";
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

export default function OmniMapPage() {
  usePageTitle("OmniMap");
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState({ display_name: "Paris, France", lat: 48.8566, lon: 2.3522 });
  const [loading, setLoading] = useState(false);

  // Routing
  const [routeMode, setRouteMode] = useState(false);
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [route, setRoute] = useState(null);
  const [profile, setProfile] = useState("driving");
  const [routing, setRouting] = useState(false);

  useEffect(() => {
    const qp = params.get("q");
    if (qp) { setQ(qp); search(null, qp); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const search = async (e, customQ) => {
    e?.preventDefault();
    const query = (customQ ?? q).trim();
    if (!query) return;
    setLoading(true); setResults([]);
    try {
      const { data } = await api.get("/map/search", { params: { q: query } });
      setResults(data.results || []);
      if ((data.results || []).length > 0) setSelected(data.results[0]);
    } finally { setLoading(false); }
  };

  const handleMapClick = (latlng) => {
    if (!routeMode) return;
    if (!from) { setFrom({ lat: latlng[0], lon: latlng[1], display_name: "Point A" }); }
    else if (!to) { setTo({ lat: latlng[0], lon: latlng[1], display_name: "Point B" }); }
    else { setFrom({ lat: latlng[0], lon: latlng[1], display_name: "Point A" }); setTo(null); setRoute(null); }
  };

  const computeRoute = async () => {
    if (!from || !to) return;
    setRouting(true); setRoute(null);
    try {
      const { data } = await api.get("/map/route", {
        params: { lat1: from.lat, lon1: from.lon, lat2: to.lat, lon2: to.lon, profile },
      });
      setRoute(data);
    } finally { setRouting(false); }
  };

  useEffect(() => { if (from && to) computeRoute(); /* eslint-disable-next-line */ }, [from, to, profile]);

  const center = selected ? [selected.lat, selected.lon] : [48.8566, 2.3522];

  return (
    <div className="min-h-screen flex flex-col" data-testid="map-page">
      <Header compact />
      <main className="flex-1 flex flex-col">
        <div className="px-4 sm:px-6 py-3 border-b border-[var(--omni-border)] flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <OmniLogo brand="map" size={32} />
            <h1 className="text-2xl font-black tracking-tight">OmniMap</h1>
          </div>
          <form onSubmit={search} className="flex-1 max-w-xl flex items-center gap-2" data-testid="map-search-form">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher un lieu, une adresse…"
                className="pl-9 rounded-full bg-[var(--omni-surface)] border-0 focus-visible:ring-2 focus-visible:ring-[#10B981]"
                data-testid="map-search-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white" data-testid="map-search-btn">
              {loading ? <Loader2 className="animate-spin" size={15} /> : <Search size={15} />}
            </Button>
          </form>
          <Button
            onClick={() => { setRouteMode((r) => !r); if (routeMode) { setFrom(null); setTo(null); setRoute(null); } }}
            variant={routeMode ? "default" : "outline"}
            className={`rounded-full ${routeMode ? "bg-[#10B981] hover:bg-[#059669] text-white" : ""}`}
            data-testid="map-route-toggle"
          >
            <Route size={14} className="mr-1" /> {routeMode ? "Quitter itinéraire" : "Itinéraire"}
          </Button>
        </div>

        <div className="flex-1 flex min-h-0">
          {/* Sidebar */}
          <aside className="w-80 border-r border-[var(--omni-border)] overflow-y-auto hidden md:block">
            {routeMode ? (
              <div className="p-4" data-testid="map-route-panel">
                <div className="text-xs font-bold uppercase text-gray-500 mb-3">Itinéraire</div>
                <p className="text-xs text-gray-500 mb-3">Cliquez sur la carte pour définir le point de départ, puis l'arrivée.</p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 text-sm" data-testid="map-route-from">
                    <span className="w-4 h-4 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span className="truncate flex-1">{from ? `${from.lat.toFixed(4)}, ${from.lon.toFixed(4)}` : "Cliquez pour définir A"}</span>
                    {from && <button onClick={() => { setFrom(null); setRoute(null); }} className="text-gray-400 hover:text-red-600"><X size={12} /></button>}
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50 text-sm" data-testid="map-route-to">
                    <span className="w-4 h-4 rounded-full bg-red-500 flex-shrink-0" />
                    <span className="truncate flex-1">{to ? `${to.lat.toFixed(4)}, ${to.lon.toFixed(4)}` : "Cliquez pour définir B"}</span>
                    {to && <button onClick={() => { setTo(null); setRoute(null); }} className="text-gray-400 hover:text-red-600"><X size={12} /></button>}
                  </div>
                </div>
                <div className="flex gap-1 mb-4">
                  {[
                    { id: "driving", icon: Car, label: "Auto" },
                    { id: "cycling", icon: Bike, label: "Vélo" },
                    { id: "walking", icon: Footprints, label: "À pied" },
                  ].map(({ id, icon: Icon, label }) => (
                    <button
                      key={id}
                      onClick={() => setProfile(id)}
                      data-testid={`map-route-profile-${id}`}
                      className={`flex-1 px-2 py-2 rounded-lg border text-xs transition flex flex-col items-center gap-1 ${profile === id ? "bg-black text-white border-black" : "border-[var(--omni-border)] hover:bg-[var(--omni-surface)]"}`}
                    >
                      <Icon size={14} /> {label}
                    </button>
                  ))}
                </div>
                {routing && <div className="text-xs text-gray-500 inline-flex items-center"><Loader2 className="animate-spin mr-1" size={12} /> Calcul…</div>}
                {route && (
                  <div className="rounded-xl bg-[var(--omni-surface)] p-3 text-sm" data-testid="map-route-result">
                    <div className="font-bold text-2xl">{formatDuration(route.duration_s)}</div>
                    <div className="text-gray-500">{formatDistance(route.distance_m)}</div>
                  </div>
                )}
              </div>
            ) : (
              <>
                {loading && (
                  <div className="p-6 text-gray-500 text-sm inline-flex items-center"><Loader2 className="animate-spin mr-2" size={14} /> Recherche…</div>
                )}
                {!loading && results.length === 0 && (
                  <div className="p-4">
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Lieux populaires</div>
                    {POPULAR.map((p) => (
                      <button
                        key={p.label}
                        onClick={() => { setQ(p.q); search(null, p.q); }}
                        className="w-full text-left px-3 py-3 rounded-lg hover:bg-[var(--omni-surface)] text-sm flex items-center gap-3"
                        data-testid={`map-popular-${p.label.toLowerCase().replace(/[^a-z]+/g,"-")}`}
                      >
                        <Compass size={16} className="text-[#10B981]" />
                        {p.label}
                      </button>
                    ))}
                  </div>
                )}
                {!loading && results.map((r, i) => (
                  <button
                    key={`${r.lat}-${r.lon}-${i}`}
                    onClick={() => setSelected(r)}
                    data-testid={`map-result-${i}`}
                    className={`w-full text-left px-4 py-3 border-b border-[var(--omni-border)] hover:bg-[var(--omni-surface)] ${selected?.lat === r.lat && selected?.lon === r.lon ? "bg-[var(--omni-surface)]" : ""}`}
                  >
                    <div className="flex items-start gap-3">
                      <MapPin size={16} className="text-[#10B981] mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{r.display_name.split(",")[0]}</div>
                        <div className="text-xs text-gray-500 truncate">{r.display_name}</div>
                        <div className="text-[10px] text-gray-400 font-mono mt-1">{r.lat.toFixed(4)}, {r.lon.toFixed(4)}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}
          </aside>

          {/* Map */}
          <div className="flex-1 relative" data-testid="map-leaflet">
            <MapContainer center={center} zoom={13} style={{ height: "100%", width: "100%" }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
              />
              <FlyTo center={selected ? [selected.lat, selected.lon] : center} />
              <MapClicker onClick={handleMapClick} />
              {!routeMode && selected && (
                <Marker position={[selected.lat, selected.lon]}>
                  <Popup>
                    <strong>{selected.display_name.split(",")[0]}</strong>
                    <br />
                    <span className="text-xs text-gray-500">{selected.display_name}</span>
                  </Popup>
                </Marker>
              )}
              {routeMode && from && (
                <Marker position={[from.lat, from.lon]}>
                  <Popup>Départ</Popup>
                </Marker>
              )}
              {routeMode && to && (
                <Marker position={[to.lat, to.lon]}>
                  <Popup>Arrivée</Popup>
                </Marker>
              )}
              {route && route.coordinates && (
                <Polyline positions={route.coordinates} color="#10B981" weight={5} opacity={0.85} />
              )}
            </MapContainer>
            {!routeMode && selected && (
              <div className="absolute bottom-4 left-4 right-4 md:left-auto md:max-w-md bg-white border border-[var(--omni-border)] rounded-xl p-4 shadow-lg z-[1000]" data-testid="map-info-card">
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-[#10B981] mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate">{selected.display_name.split(",")[0]}</div>
                    <div className="text-xs text-gray-500 line-clamp-2">{selected.display_name}</div>
                  </div>
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${selected.lat}&mlon=${selected.lon}#map=14/${selected.lat}/${selected.lon}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs px-3 py-1.5 rounded-full bg-[#10B981] hover:bg-[#059669] text-white inline-flex items-center gap-1"
                    data-testid="map-directions-link"
                  >
                    <Navigation size={12} /> OSM
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
